/**
 * Reading MAVLink off a USB radio in the browser, via the Web Serial API.
 *
 * A containerised backend cannot open a laptop's COM port, so a serial radio
 * used to need a host-side bridge process. Web Serial lets the browser open the
 * port directly instead — the operator picks the device once, and the page reads
 * the byte stream. This module is the port half: feature detection, opening the
 * port, naming the device, splitting the raw bytes into whole MAVLink frames,
 * and deciding whether what came back is actually an aircraft.
 *
 * The frames go to the server over `/ws/serial-bridge`, which is wired up in
 * serialBridgeClient.ts. Web Serial is a Chromium-only API; the connector says
 * so rather than offering a control that cannot work.
 */

//: MAVLink 2 frame: 0xFD, then a payload length byte at offset 1, then a
//: 10-byte header total, payload, 2-byte CRC, and an optional 13-byte signature
//: when the incompat flag (offset 2) has bit 0 set. v1 frames (0xFE) are a
//: 6-byte header + payload + CRC.
const MAVLINK2_STX = 0xfd;
const MAVLINK1_STX = 0xfe;
const MAVLINK_MSG_ID_HEARTBEAT = 0;

export interface Frame {
  bytes: Uint8Array;
}

/**
 * A streaming splitter: feed it arbitrary byte chunks, get back whole frames.
 *
 * Serial reads arrive in chunks that split frames anywhere, so the boundary
 * logic has to hold partial state across calls. It resyncs on a bad start byte
 * rather than throwing — a noisy radio drops bytes, and one garbled frame must
 * not stall the stream.
 */
export class MavlinkFramer {
  private buffer: number[] = [];

  push(chunk: Uint8Array): Frame[] {
    for (const byte of chunk) this.buffer.push(byte);
    const frames: Frame[] = [];

    for (;;) {
      // Drop leading noise until a start-of-frame byte.
      while (this.buffer.length > 0 && this.buffer[0] !== MAVLINK2_STX && this.buffer[0] !== MAVLINK1_STX) {
        this.buffer.shift();
      }
      if (this.buffer.length < 2) break;

      const stx = this.buffer[0]!;
      const payloadLen = this.buffer[1]!;
      let frameLen: number;
      if (stx === MAVLINK2_STX) {
        const incompat = this.buffer.length >= 3 ? this.buffer[2]! : 0;
        if (this.buffer.length < 3) break; // need the incompat flag to size a signed frame
        const signed = (incompat & 0x01) !== 0;
        frameLen = 10 + payloadLen + 2 + (signed ? 13 : 0);
      } else {
        frameLen = 6 + payloadLen + 2;
      }

      if (this.buffer.length < frameLen) break; // wait for the rest of the frame
      frames.push({ bytes: Uint8Array.from(this.buffer.slice(0, frameLen)) });
      this.buffer = this.buffer.slice(frameLen);
    }
    return frames;
  }

  /** Bytes held pending a complete frame — for a stall check. */
  get pending(): number {
    return this.buffer.length;
  }
}

export interface FrameHeader {
  sysId: number;
  compId: number;
  msgId: number;
}

/**
 * Who sent a frame and what it is, read straight off the header bytes.
 *
 * v2: seq at 4, sysid 5, compid 6, then a 24-bit little-endian message id.
 * v1: seq at 2, sysid 3, compid 4, then a single-byte message id.
 */
export function frameHeader(bytes: Uint8Array): FrameHeader | null {
  if (bytes[0] === MAVLINK2_STX && bytes.length >= 10) {
    return {
      sysId: bytes[5]!,
      compId: bytes[6]!,
      msgId: bytes[7]! | (bytes[8]! << 8) | (bytes[9]! << 16),
    };
  }
  if (bytes[0] === MAVLINK1_STX && bytes.length >= 6) {
    return { sysId: bytes[3]!, compId: bytes[4]!, msgId: bytes[5]! };
  }
  return null;
}

export type ProbeVerdict = 'silent' | 'noise' | 'found';

export interface ProbeStatus {
  bytes: number;
  frames: number;
  sysId: number | null;
  verdict: ProbeVerdict;
}

/**
 * What the port is actually producing, while the operator watches.
 *
 * The distinction between the three verdicts is the whole value. The reference
 * GCS declared success as soon as *any* byte arrived, so a radio at the wrong
 * baud — which produces a healthy-looking stream of garbage — reported a
 * connected aircraft that then never appeared. A heartbeat, and nothing less,
 * means an aircraft; bytes without one means the baud rate is wrong; no bytes
 * at all means the radio is silent or the wrong port was picked.
 */
export class HeartbeatProbe {
  private framer = new MavlinkFramer();
  private bytes = 0;
  private frames = 0;
  private sysId: number | null = null;

  /** Feed raw serial bytes. Returns the whole frames they completed. */
  push(chunk: Uint8Array): Frame[] {
    this.bytes += chunk.length;
    const frames = this.framer.push(chunk);
    this.frames += frames.length;
    for (const frame of frames) {
      const header = frameHeader(frame.bytes);
      if (header !== null && header.msgId === MAVLINK_MSG_ID_HEARTBEAT && this.sysId === null) {
        this.sysId = header.sysId;
      }
    }
    return frames;
  }

  status(): ProbeStatus {
    return {
      bytes: this.bytes,
      frames: this.frames,
      sysId: this.sysId,
      verdict: this.sysId !== null ? 'found' : this.bytes === 0 ? 'silent' : 'noise',
    };
  }
}

/** One line an operator can act on, for each of the three verdicts. */
export function probeSummary(status: ProbeStatus): string {
  if (status.verdict === 'found') return `Aircraft found — sysid ${status.sysId}`;
  if (status.verdict === 'silent') return 'No bytes yet — is the radio powered and on this port?';
  return 'Bytes arriving but no MAVLink frames — check the baud rate';
}

export interface SerialPortInfo {
  usbVendorId?: number;
  usbProductId?: number;
}

/**
 * USB vendor ids an operator will actually plug in.
 *
 * A COM number is not an identity: on a laptop with three adapters "COM3" tells
 * the operator nothing about which radio they just picked. The vendor id does.
 * 0x1209 is the shared open-hardware id (pid.codes) used by dozens of unrelated
 * boards, so it is labelled generically rather than guessed at.
 */
const USB_VENDORS: Record<number, string> = {
  0x0403: 'FTDI — RFD900 / SiK radio',
  0x10c4: 'Silicon Labs CP210x — SiK radio / autopilot',
  0x1a86: 'CH340 serial adapter',
  0x067b: 'Prolific serial adapter',
  0x1546: 'u-blox',
  0x26ac: '3DR / mRo',
  0x2dae: 'CubePilot',
  0x1209: 'Open-hardware USB serial',
};

function hex4(value: number): string {
  return value.toString(16).padStart(4, '0');
}

/** A human name for a port, from whatever the browser is willing to say. */
export function describeSerialPort(info: SerialPortInfo): string {
  const { usbVendorId, usbProductId } = info;
  if (usbVendorId === undefined) return 'Serial port';
  const name = USB_VENDORS[usbVendorId] ?? 'USB serial device';
  const ids =
    usbProductId === undefined ? hex4(usbVendorId) : `${hex4(usbVendorId)}:${hex4(usbProductId)}`;
  return `${name} (${ids})`;
}

export function webSerialSupported(): boolean {
  return typeof navigator !== 'undefined' && 'serial' in navigator;
}

interface SerialPortLike {
  open: (options: { baudRate: number }) => Promise<void>;
  close: () => Promise<void>;
  getInfo: () => SerialPortInfo;
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
}

export interface SerialSession {
  label: string;
  info: SerialPortInfo;
  /** Raw reads, in whatever sizes the port produces them. */
  chunks: AsyncGenerator<Uint8Array>;
  /** Back out to the radio: commands, the GCS heartbeat, mission items. */
  write: (data: Uint8Array) => Promise<void>;
  close: () => Promise<void>;
}

/**
 * Prompt for a serial port and open it in both directions.
 *
 * Bidirectional on purpose: a read-only link puts the aircraft on the map and
 * then refuses every command, which is worse than no link at all because it
 * looks like one. Throws if Web Serial is absent so the caller surfaces it
 * rather than hanging on a promise that never opens.
 */
export async function openSerialMavlink(baudRate = 57600): Promise<SerialSession> {
  if (!webSerialSupported()) {
    throw new Error('Web Serial is not available in this browser.');
  }
  const nav = navigator as unknown as { serial: { requestPort: () => Promise<SerialPortLike> } };
  const port = await nav.serial.requestPort();
  await port.open({ baudRate });
  if (port.readable === null || port.writable === null) {
    await port.close().catch(() => {});
    throw new Error('The port opened but gave no stream. Unplug it and try again.');
  }
  const reader = port.readable.getReader();
  const writer = port.writable.getWriter();

  async function* stream(): AsyncGenerator<Uint8Array> {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) return;
      if (value) yield value;
    }
  }

  return {
    label: describeSerialPort(port.getInfo()),
    info: port.getInfo(),
    chunks: stream(),
    write: (data) => writer.write(data),
    close: async () => {
      await reader.cancel().catch(() => {});
      reader.releaseLock();
      await writer.close().catch(() => {});
      writer.releaseLock();
      await port.close().catch(() => {});
    },
  };
}
